import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import { db } from '../../lib/firebase';

/**
 * `participants/{uid}/memberships/{districtId}` の `consentAcceptedAt` 有無で
 * 「ユーザー設定が済んでいるか」を判定する hook (old2 投稿フロー移植 Phase E)。
 *
 * old2 `apps/user/src/pages/tabs/PostTab.tsx` L65 + L118-137 の profileStatus 判定を逐語移植:
 *   - membership doc が無い → `missing` (= 未設定)
 *   - doc はあるが `consentAcceptedAt` が無い → `missing`
 *   - `consentAcceptedAt` あり → `set`
 *   - 読込中 / 読込失敗 → `unknown` (= 未確認、PostForm 側では incomplete 扱いしない)
 *
 * PostForm は `incomplete === true` のときに警告バナーを表示し、投稿を不可にする。
 * `unknown` (読込中 / uid 未確定) では誤って投稿をブロックしないよう incomplete=false 扱い
 * (old2 の `userSettingsIncomplete = profileStatus === 'missing'` と同義)。
 */
export type ProfileStatus = 'unknown' | 'set' | 'missing';

export interface UseMembershipProfileStatusReturn {
  status: ProfileStatus;
  /** `status === 'missing'` のショートカット (PostForm の `userSettingsIncomplete` に直結)。 */
  incomplete: boolean;
}

export function useMembershipProfileStatus(
  uid: string | null,
  districtId: string | null,
): UseMembershipProfileStatusReturn {
  const [status, setStatus] = useState<ProfileStatus>('unknown');
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    if (!uid || !districtId) {
      setStatus('unknown');
      lastKey.current = null;
      return;
    }
    const key = `${uid}/${districtId}`;
    if (lastKey.current === key) return;
    lastKey.current = key;

    let cancelled = false;
    (async () => {
      try {
        const ref = doc(db, 'participants', uid, 'memberships', districtId);
        const snap = await getDoc(ref);
        if (cancelled) return;
        if (!snap.exists()) {
          setStatus('missing');
          return;
        }
        const data = snap.data();
        setStatus(data.consentAcceptedAt ? 'set' : 'missing');
      } catch {
        // old2 L134-136: 読込失敗時は未確認 (unknown) のままにして投稿をブロックしない。
        if (!cancelled) setStatus('unknown');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uid, districtId]);

  return { status, incomplete: status === 'missing' };
}
